import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Archive, Check, MessageCircle } from "lucide-react";
import { useState } from "react";
import { InfiniteScrollTrigger } from "../../components/InfiniteScrollTrigger";
import { api } from "../../lib/api";
import { useAuth } from "../../state/auth";

type HelpMessage = {
  publicId: string; name: string; contact: string; message: string;
  context: string | null; status: "NEW" | "READ" | "ARCHIVED"; createdAt: string;
};
type MessagePage = { items: HelpMessage[]; total: number; offset: number; hasMore: boolean };

export function AdminHelpMessagesPage() {
  const { accessToken } = useAuth();
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<"NEW" | "READ" | "ARCHIVED">("NEW");
  const messages = useInfiniteQuery({
    queryKey: ["admin", "help-messages", status],
    initialPageParam: 0,
    queryFn: ({ pageParam }) => api<MessagePage>(`/admin/help-messages?status=${status}&limit=20&offset=${pageParam}`, { token: accessToken }),
    getNextPageParam: (last) => last.hasMore ? last.offset + last.items.length : undefined
  });
  const items = messages.data?.pages.flatMap((page) => page.items) ?? [];
  const total = messages.data?.pages[0]?.total ?? 0;
  const setMessageStatus = useMutation({
    mutationFn: ({ publicId, next }: { publicId: string; next: "READ" | "ARCHIVED" }) => api(`/admin/help-messages/${publicId}`, {
      method: "PATCH", token: accessToken, body: JSON.stringify({ status: next })
    }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ["admin", "help-messages"] })
  });

  return <>
    <div className="flex flex-wrap items-end justify-between gap-4">
      <div><p className="text-sm font-extrabold uppercase tracking-widest text-clay-dark">Customer questions</p><h1 className="mt-2 font-display text-4xl font-bold">Help inbox</h1><p className="mt-2 text-ink/55">Messages left through the site's help widget.</p></div>
      <span className="rounded-full bg-butter/30 px-4 py-2 font-extrabold">{total} {status.toLowerCase()}</span>
    </div>
    <div className="mt-6 flex flex-wrap gap-2">
      {(["NEW", "READ", "ARCHIVED"] as const).map((itemStatus) => <button key={itemStatus} onClick={() => setStatus(itemStatus)} className={`rounded-full px-4 py-2 text-sm font-extrabold ${status === itemStatus ? "bg-ink text-white" : "border border-ink/10 bg-white"}`}>{itemStatus.toLowerCase()}</button>)}
    </div>
    <div className="mt-5 grid gap-3">
      {messages.isLoading && <p className="p-8 text-center text-ink/50">Loading messages…</p>}
      {items.map((item) => <div key={item.publicId} className="rounded-[1.75rem] border border-ink/10 bg-white p-5">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div><p className="font-extrabold">{item.name}</p><p className="text-xs text-ink/45">{item.contact} · {new Date(item.createdAt).toLocaleString()}</p></div>
          <div className="flex gap-2">
            {item.status === "NEW" && <button onClick={() => setMessageStatus.mutate({ publicId: item.publicId, next: "READ" })} className="btn-secondary min-h-9 px-3 py-1.5 text-xs"><Check size={14} /> Mark read</button>}
            {item.status !== "ARCHIVED" && <button onClick={() => setMessageStatus.mutate({ publicId: item.publicId, next: "ARCHIVED" })} className="btn-secondary min-h-9 px-3 py-1.5 text-xs"><Archive size={14} /> Archive</button>}
          </div>
        </div>
        <p className="mt-3 text-sm leading-relaxed text-ink/75">{item.message}</p>
        {item.context && <p className="mt-2 text-xs text-ink/40">Asked before messaging: “{item.context}”</p>}
      </div>)}
      {!messages.isLoading && items.length === 0 && <div className="rounded-[1.75rem] border border-ink/10 bg-white p-12 text-center"><MessageCircle className="mx-auto text-sage" size={34} /><p className="mt-3 font-display text-2xl font-bold">No messages here</p><p className="text-sm text-ink/50">New messages appear automatically.</p></div>}
    </div>
    <InfiniteScrollTrigger hasMore={messages.hasNextPage} loading={messages.isFetchingNextPage} onLoadMore={() => void messages.fetchNextPage()} />
  </>;
}
